import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import {
   HISTORICAL_LOAD_UNTIL,
   THRESHOLD_OPTIONS,
   VINTAGE_COLORS,
} from '../constants';
import {
   formatCurrency,
   formatMonthLabel,
   formatNumber,
   formatPercent,
} from '../utils/format';
import {
   buildPlottedSafras,
   buildScale,
   defaultPlottedSafras,
   heatStyle,
   plottablePointCount,
} from '../utils/vintage';
import {
   CardHead,
   CardSubtitle,
   CardTitle,
   ChartCaption,
   EmptyText,
   HeatCell,
   HeatCellEmpty,
   HeatRow,
   HeatTable,
   LegendButton,
   LegendDash,
   LegendSafra,
   LegendTitulos,
   Refetching,
   SafraBadge,
   SafraCell,
   SafraToggle,
   Segmented,
   SegmentedButton,
   TableWrap,
   VintageGrid,
   VintageLegend,
} from '../styles';
import { blockShape } from '../utils/shapes';
import { BlockErrorState, BlockSkeleton } from './BlockState';
import VintageChart from './VintageChart';

// ---------------------------------------------------------------------------
// Dobra 5 · Safra (vintage).
//
// Para cada mês de emissão, quanto do valor emitido atingiu `threshold_dias`+
// de atraso em M+1, M+3, M+6 e M+12. É a métrica que responde "a qualidade da
// carteira está melhorando ou piorando ao longo do tempo?".
//
// Duas superfícies para o mesmo dado, de propósito:
//  · o GRÁFICO mostra a forma das safras recentes (limitado — ver
//    `selectPlottedSafras`);
//  · o HEATMAP mostra TODAS as safras, com o número exato em cada célula.
// Nenhuma safra some da tela; o que o gráfico corta, a tabela carrega.
//
// Trocar o limiar recarrega só este bloco.
// ---------------------------------------------------------------------------

export default function VintagePanel({
   block,
   thresholdDias,
   onChangeThreshold,
   onRetry,
   view,
   onViewChange,
}) {
   const { data } = block;

   // A escolha de safras e o isolamento moram FORA do painel: ele é montado e
   // desmontado ao trocar de aba, e perder a comparação que a pessoa acabou de
   // montar seria o pior efeito colateral possível da divisão em abas.
   //
   // `selectedKeys: null` = ainda não escolheram; cai no default. Assim que o
   // operador mexe, a escolha dele manda — inclusive se ele esvaziar o gráfico.
   const { selectedKeys, isolated } = view;

   const setIsolated = (updater) =>
      onViewChange((current) => ({
         ...current,
         isolated:
            typeof updater === 'function' ? updater(current.isolated) : updater,
      }));

   const safras = useMemo(() => data?.safras ?? [], [data]);
   const months = data?.months ?? [];

   // Trocar a régua muda quais safras têm ponto suficiente, então a sugestão é
   // recalculada — mas só enquanto o operador não fez a própria seleção.
   const effectiveKeys = useMemo(
      () => selectedKeys ?? defaultPlottedSafras(safras),
      [selectedKeys, safras],
   );

   const plotted = useMemo(
      () => buildPlottedSafras(safras, effectiveKeys),
      [safras, effectiveKeys],
   );
   const scale = useMemo(() => buildScale(plotted), [plotted]);

   const handleIsolate = (safra) => {
      setIsolated((current) => (current === safra ? null : safra));
   };

   const toggleSafra = (safraKey) => {
      onViewChange((current) => {
         const base = current.selectedKeys ?? defaultPlottedSafras(safras);
         return {
            selectedKeys: base.includes(safraKey)
               ? base.filter((key) => key !== safraKey)
               : [...base, safraKey],
            // Isolar uma safra que acabou de sair do gráfico deixaria as outras
            // esmaecidas sem motivo visível.
            isolated: null,
         };
      });
   };

   const atColorLimit = plotted.length >= VINTAGE_COLORS.length;

   const renderBody = () => {
      if (block.loading && !data) return <BlockSkeleton rows={4} height={52} />;
      if (block.error) {
         return <BlockErrorState error={block.error} onRetry={onRetry} />;
      }
      if (!safras.length) {
         return (
            <EmptyText>
               Nenhuma safra no recorte. Meses sem emissão são períodos
               comerciais reais, não falha de coleta.
            </EmptyText>
         );
      }

      return (
         <>
            <VintageGrid>
               {plotted.length ? (
                  <VintageChart
                     safras={plotted}
                     scale={scale}
                     months={months}
                     isolated={isolated}
                     onIsolate={handleIsolate}
                  />
               ) : (
                  <EmptyText>
                     Nenhuma safra no gráfico. Clique numa linha da tabela abaixo
                     para plotá-la — safras com um único ponto não formam curva.
                  </EmptyText>
               )}

               <VintageLegend>
                  <ChartCaption>
                     Clique numa safra da tabela abaixo para incluí-la ou tirá-la
                     do gráfico.
                  </ChartCaption>
                  {plotted.map((safra) => (
                     <LegendButton
                        key={safra.safra}
                        type="button"
                        $active={isolated === safra.safra}
                        $dimmed={Boolean(isolated) && isolated !== safra.safra}
                        aria-pressed={isolated === safra.safra}
                        onClick={() => handleIsolate(safra.safra)}
                     >
                        <LegendDash
                           $color={safra.color}
                           $dashed={safra.isHistorical}
                        />
                        <LegendSafra>{formatMonthLabel(safra.safra)}</LegendSafra>
                        <LegendTitulos>
                           {formatNumber(safra.emitido?.titulos)} tít.
                        </LegendTitulos>
                     </LegendButton>
                  ))}
                  {plotted.some((safra) => safra.isHistorical) ? (
                     <ChartCaption>
                        Linha tracejada = safra da carga histórica do ASAAS (até{' '}
                        {formatMonthLabel(HISTORICAL_LOAD_UNTIL)}), importada sem
                        departamento gerador.
                     </ChartCaption>
                  ) : null}
               </VintageLegend>
            </VintageGrid>

            <TableWrap>
               <HeatTable>
                  <thead>
                     <tr>
                        <th>Safra</th>
                        <th>Emitido</th>
                        {months.map((month) => (
                           <th key={month}>M+{month}</th>
                        ))}
                     </tr>
                  </thead>
                  <tbody>
                     {safras.map((safra) => {
                        const isHistorical = safra.origem === 'carga_historica';
                        const byMonth = new Map(
                           (safra.pontos ?? []).map((point) => [point.m, point]),
                        );
                        const isPlotted = effectiveKeys.includes(safra.safra);
                        const points = plottablePointCount(safra);
                        // Safra com menos de 2 pontos não vira linha; deixar o
                        // botão ativo prometeria uma curva que não aparece.
                        const plottable = points >= 2;
                        const blockedByLimit = !isPlotted && atColorLimit;

                        return (
                           <HeatRow key={safra.safra} $selected={isPlotted}>
                              <td>
                                 <SafraToggle
                                    type="button"
                                    $selected={isPlotted}
                                    aria-pressed={isPlotted}
                                    disabled={!plottable || blockedByLimit}
                                    title={
                                       (!plottable &&
                                          'Safra nova demais para formar curva: precisa de ao menos 2 pontos medidos.') ||
                                       (blockedByLimit &&
                                          `O gráfico comporta ${VINTAGE_COLORS.length} safras. Tire uma para incluir outra.`) ||
                                       (isPlotted
                                          ? 'Tirar do gráfico'
                                          : 'Incluir no gráfico')
                                    }
                                    onClick={() => toggleSafra(safra.safra)}
                                 >
                                    <SafraCell>
                                       {formatMonthLabel(safra.safra)}
                                       <SafraBadge $historical={isHistorical}>
                                          {isHistorical ? 'histórica' : 'MWA'}
                                       </SafraBadge>
                                    </SafraCell>
                                 </SafraToggle>
                              </td>
                              <td>{formatCurrency(safra.emitido?.valor)}</td>
                              {months.map((month) => {
                                 const point = byMonth.get(month);
                                 const pct = point?.pct ?? null;
                                 const style = heatStyle(pct);

                                 if (!style) {
                                    return (
                                       <td key={month}>
                                          <HeatCellEmpty
                                             title={`M+${month} ainda não ocorreu para a safra de ${formatMonthLabel(safra.safra)}.`}
                                          >
                                             —
                                          </HeatCellEmpty>
                                       </td>
                                    );
                                 }

                                 return (
                                    <td key={month}>
                                       <HeatCell
                                          style={style}
                                          title={`${formatMonthLabel(safra.safra)} · M+${month} · ${formatNumber(point.titulos)} títulos · ${formatCurrency(point.valor)}`}
                                       >
                                          {formatPercent(pct, 1)}
                                       </HeatCell>
                                    </td>
                                 );
                              })}
                           </HeatRow>
                        );
                     })}
                  </tbody>
               </HeatTable>
            </TableWrap>
         </>
      );
   };

   const plottedNote = safras.length
      ? ` · ${plotted.length} no gráfico, ${safras.length} na tabela`
      : '';

   return (
      <>
         <CardHead>
            <div>
               <CardTitle as="h3">Safra</CardTitle>
               <CardSubtitle>
                  % do valor emitido de cada safra que atingiu {thresholdDias}+
                  dias de atraso em{' '}
                  {months.map((month) => `M+${month}`).join(', ')}
                  {plottedNote}
               </CardSubtitle>
            </div>
            <Segmented role="group" aria-label="Limiar de atraso">
               {THRESHOLD_OPTIONS.map((option) => (
                  <SegmentedButton
                     key={option}
                     type="button"
                     $active={thresholdDias === option}
                     aria-pressed={thresholdDias === option}
                     onClick={() => onChangeThreshold(option)}
                  >
                     {option}d
                  </SegmentedButton>
               ))}
            </Segmented>
         </CardHead>

         <Refetching $busy={block.loading && Boolean(data)}>
            {renderBody()}
         </Refetching>
      </>
   );
}

VintagePanel.propTypes = {
   block: blockShape(
      PropTypes.shape({
         threshold_dias: PropTypes.number,
         months: PropTypes.arrayOf(PropTypes.number),
         safras: PropTypes.arrayOf(
            PropTypes.shape({
               safra: PropTypes.string,
               origem: PropTypes.string,
               emitido: PropTypes.shape({
                  titulos: PropTypes.number,
                  valor: PropTypes.number,
               }),
               pontos: PropTypes.arrayOf(PropTypes.shape({})),
            }),
         ),
      }),
   ).isRequired,
   thresholdDias: PropTypes.number.isRequired,
   onChangeThreshold: PropTypes.func.isRequired,
   onRetry: PropTypes.func.isRequired,
   view: PropTypes.shape({
      selectedKeys: PropTypes.arrayOf(PropTypes.string),
      isolated: PropTypes.string,
   }).isRequired,
   onViewChange: PropTypes.func.isRequired,
};
